import {
  defaultLocale,
  type Locale,
  localeMeta,
  localePath,
  locales,
} from "@/i18n/locales";

export const SITE_URL = "https://vaxy.app";
export const SITE_NAME = "Vaxy";
export const GA_MEASUREMENT_ID = "G-TSTN13FVLH";

export const CONTACT_EMAIL = "info@vaxy.app";
export const LEGAL_EMAIL = "info@vaccinatieboekje.com";

const appStoreUrls = {
  nl: "https://apps.apple.com/nl/app/vaxy-vaccinatieboekje/id1229465470",
  en: "https://apps.apple.com/us/app/vaxy-vaccination-record/id1229465470",
  de: "https://apps.apple.com/ch/app/vaxy-impfpass/id1229465470",
  fr: "https://apps.apple.com/fr/app/vaxy-carnet-de-vaccination/id1229465470",
  es: "https://apps.apple.com/es/app/vaxy-cartilla-de-vacunaci%C3%B3n/id1229465470",
} as const satisfies Record<Locale, string>;

export function appStoreUrl(locale: Locale): string {
  return appStoreUrls[locale];
}

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.vaccinaties";

export function absoluteUrl(path: string): string {
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function canonicalFor(locale: Locale, path: string): string {
  return absoluteUrl(localePath(locale, path));
}

export function hreflangLinks(path: string) {
  const suffix = path === "/" ? "/" : path;
  return [
    ...locales.map((locale) => ({
      rel: "alternate" as const,
      hrefLang: localeMeta[locale].html,
      href: canonicalFor(locale, suffix),
    })),
    {
      rel: "alternate" as const,
      hrefLang: "x-default",
      href: canonicalFor(defaultLocale, suffix),
    },
  ];
}

export function seoHead(opts: {
  locale: Locale;
  path: string;
  title: string;
  description: string;
  image?: string;
  type?: "website" | "article";
  published?: string;
  smartBanner?: boolean;
  noIndex?: boolean;
}) {
  const url = canonicalFor(opts.locale, opts.path);
  const image = absoluteUrl(opts.image ?? "/og.png");
  return {
    meta: [
      { title: opts.title },
      { name: "description", content: opts.description },
      ...(opts.noIndex
        ? [{ name: "robots", content: "noindex, nofollow" }]
        : []),
      { property: "og:type", content: opts.type ?? "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: opts.description },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:locale", content: localeMeta[opts.locale].og },
      ...locales
        .filter((locale) => locale !== opts.locale)
        .map((locale) => ({
          property: "og:locale:alternate",
          content: localeMeta[locale].og,
        })),
      ...(opts.published
        ? [{ property: "article:published_time", content: opts.published }]
        : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: opts.title },
      { name: "twitter:description", content: opts.description },
      { name: "twitter:image", content: image },
      ...(opts.smartBanner
        ? [{ name: "apple-itunes-app", content: "app-id=1229465470" }]
        : []),
    ],
    links: [{ rel: "canonical", href: url }, ...hreflangLinks(opts.path)],
  };
}
